import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { MotionValue } from 'framer-motion';

/**
 * "Entorno" — the condomínio as a character in the story, not a
 * feature list. Four prose vignettes that each carry a different
 * sense memory: the way in, the land, the club, the discipline.
 *
 * Rule (Wave 44.8): every factual claim here maps to either the
 * Memorial Descritivo, the condomínio's public presentation of
 * Ninho Verde I (Porangaba-SP · Rodovia Castello Branco km 157 ·
 * Clube de Campo + sistema de Lagos as DISTINCT amenities — they
 * are two separate things, JP corrected this in Wave 76), or the
 * project drawings. No invented hectares, no invented house counts,
 * no Vinhedo, no Mata Atlântica claim — only what we can defend on
 * a phone call with a skeptical buyer.
 *
 * Technique: an anchored, centered column that sweeps through four
 * pinned stops. Each stop reveals its own paragraph with a slow
 * word-fade — reads like turning pages of a catalog rather than
 * scrolling a checklist of amenities.
 */
export function Condo() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // Four vignettes, each gets a quarter of the scroll.
  // We use envelope() below to compute per-stop in/hold/out curves.
  const o1 = useStopOpacity(scrollYProgress, 0.05, 0.28);
  const o2 = useStopOpacity(scrollYProgress, 0.30, 0.50);
  const o3 = useStopOpacity(scrollYProgress, 0.52, 0.72);
  const o4 = useStopOpacity(scrollYProgress, 0.74, 0.94);

  // Slight downward drift on each stop so movement feels alive.
  const y1 = useStopY(scrollYProgress, 0.05, 0.28);
  const y2 = useStopY(scrollYProgress, 0.30, 0.50);
  const y3 = useStopY(scrollYProgress, 0.52, 0.72);
  const y4 = useStopY(scrollYProgress, 0.74, 0.94);

  return (
    <section
      ref={ref}
      className="relative h-[420vh] border-t border-line bg-bg"
      aria-label="O entorno da casa"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(212,184,150,0.05), transparent 70%)',
          }}
        />

        <div className="mx-auto w-full max-w-3xl px-6 text-center sm:px-12">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Ninho Verde I · Porangaba / SP
          </p>
          <h2 className="mt-5 font-display text-[clamp(2rem,5vw,3rem)] font-light leading-[1.05] tracking-tighter text-ink [text-wrap:balance]">
            Km 157 da Castello Branco. <br />
            O ritmo muda na portaria.
          </h2>
        </div>

        {/* Stacked vignettes — only one is legible at a time */}
        <div className="relative mt-12 flex-1">
          <Vignette opacity={o1} y={y1} kicker="A CAMINHO">
            Ninho Verde I fica em Porangaba, interior de São Paulo, com acesso pelo km 157 da Rodovia Castello Branco. Cerca de uma hora e meia da capital pela SP-280. Na portaria, o ritmo muda.
          </Vignette>
          <Vignette opacity={o2} y={y2} kicker="A TERRA">
            Relevo ondulado, vegetação nativa que permanece. A casa foi implantada respeitando as árvores existentes e a cota natural do lote. Nada foi nivelado à força — a topografia mandou, o projeto ouviu.
          </Vignette>
          <Vignette opacity={o3} y={y3} kicker="LAGOS · CLUBE">
            Os lagos atravessam o loteamento — espelhos d'água perenes, trilhas que contornam a margem, fauna que aprendeu a coexistir. O Clube de Campo é um ponto separado: piscina, quadras, salão de festas, sede social. Duas ofertas distintas, manutenção pelo condomínio, uso pelos moradores.
          </Vignette>
          <Vignette opacity={o4} y={y4} kicker="A DISCIPLINA">
            Portaria 24 horas, ronda interna, regras claras de edificação no regulamento do loteamento. Cada obra tem tapume metálico, placa identificando autor e responsável técnico, câmera gravando. Vizinhança com contrato — não só com cerca.
          </Vignette>
        </div>
      </div>
    </section>
  );
}

function Vignette({
  opacity,
  y,
  kicker,
  children,
}: {
  opacity: MotionValue<number>;
  y: MotionValue<string>;
  kicker: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      className="absolute inset-0 flex items-start justify-center px-6 pt-6 sm:px-12"
      style={{ opacity, y }}
    >
      <div className="max-w-[44ch] text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent/80">
          {kicker}
        </p>
        <p className="mt-6 font-display text-[clamp(1.5rem,3.4vw,2.5rem)] font-light leading-[1.25] tracking-tight text-ink [text-wrap:balance]">
          {children}
        </p>
      </div>
    </motion.div>
  );
}

// ───────────────────────── stop envelope helpers ─────────────────────────

function useStopOpacity(
  progress: MotionValue<number>,
  start: number,
  end: number,
): MotionValue<number> {
  const fadeIn = start + (end - start) * 0.2;
  const fadeOut = start + (end - start) * 0.8;
  return useTransform(progress, [start, fadeIn, fadeOut, end], [0, 1, 1, 0]);
}

function useStopY(
  progress: MotionValue<number>,
  start: number,
  end: number,
): MotionValue<string> {
  return useTransform(progress, [start, end], ['30px', '-20px']);
}
